import { Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CURRENCY_REGEX } from "../../../../utils/currencyUtils";

import type { UtilitiesParams } from "../../../../utils/Types";
import useBudgetHelpers from "../../../../utils/useBudgetHelpers/useBudgetHelpers";
import useNavigateHelpers from "../../../../utils/useNavigateHelpers/useNavigateHelpers";
import usePostIntent from "../../../../utils/usePostIntent/usePostIntent";
import FormNavButtons from "../../../Common/FormNavButtons/FormNavButtons";
import FormTitle from "../../../Common/FormTitle/FormTitle";
import TextFieldGroup from "../../../Common/TextFieldGroup/TextFieldGroup";

export function Utilities() {
	const { t: global } = useTranslation("global");
	const { t } = useTranslation("expenses");

	const { postStatus, postFormData } = usePostIntent();
	const { generateDefaultValues } = useBudgetHelpers();

	const {
		handleSubmit,
		control,
		formState: { isDirty },
	} = useForm<UtilitiesParams>({
		defaultValues: generateDefaultValues<UtilitiesParams>([
			"Electricity",
			"Gas/Oil/Heat",
			"Water",
		]),
	});

	const { navigateNextPrompt } = useNavigateHelpers();

	const submitForm = (data: UtilitiesParams) => {
		if (isDirty) {
			const CATEGORY = "Utilities";
			postFormData(data, CATEGORY).then(() => {
				postStatus();
				navigateNextPrompt();
			});
		} else {
			postStatus();
			navigateNextPrompt();
		}
	};

	return (
		<>
			<FormTitle faIcon={["fas", "plug-circle-bolt"]}>
				{t("utilities.formTitle")}
			</FormTitle>
			<p className="text-navy-blue italic text-small">{global("monthly")}</p>
			<Form onSubmit={handleSubmit(submitForm)} noValidate>
				<Row>
					<Col>
						<TextFieldGroup
							label={t("utilities.electricity.label")}
							rules={{
								pattern: {
									value: CURRENCY_REGEX,
									message: global("rules.dollarAmount"),
								},
								max: {
									value: 10000,
									message: global("rules.max", { amount: 10000 }),
								},
								min: {
									value: 0,
									message: global("rules.min"),
								},
							}}
							name="Electricity"
							prefix="$"
							className="field-xs"
							type="number"
							control={control}
						/>
					</Col>
				</Row>
				<Row className="mt-3">
					<Col>
						<TextFieldGroup
							label={t("utilities.water.label")}
							rules={{
								pattern: {
									value: CURRENCY_REGEX,
									message: global("rules.dollarAmount"),
								},
								max: {
									value: 10000,
									message: global("rules.max", { amount: 10000 }),
								},
								min: {
									value: 0,
									message: global("rules.min"),
								},
							}}
							name="Water"
							prefix="$"
							className="field-xs"
							type="number"
							control={control}
						/>
					</Col>
				</Row>
				<Row className="mt-3">
					<Col>
						<TextFieldGroup
							label={t("utilities.heat.label")}
							rules={{
								pattern: {
									value: CURRENCY_REGEX,
									message: global("rules.dollarAmount"),
								},
								max: {
									value: 10000,
									message: global("rules.max", { amount: 10000 }),
								},
								min: {
									value: 0,
									message: global("rules.min"),
								},
							}}
							name="Gas/Oil/Heat"
							prefix="$"
							className="field-xs"
							type="number"
							control={control}
						/>
					</Col>
				</Row>
				<FormNavButtons />
			</Form>
		</>
	);
}
